import React, { useEffect, useRef, useState } from 'react';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { API_BASE, intakeApi, isRequired } from '../utils/jobIntake';

// Inputs of the HM job form (one per input "kind" in app/services/job_intake_fields.py).

async function geoApi(path) {
  const res = await fetch(`${API_BASE}/api/geo${path}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
  if (!res.ok) throw new Error(`geo ${res.status}`);
  return res.json();
}
const geoCache = {};
export const cachedGeo = (path) => { if (!geoCache[path]) geoCache[path] = geoApi(path).catch((e) => { delete geoCache[path]; throw e; }); return geoCache[path]; };

export const timeLabel = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};
export const todayIso = () => { const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 10); };
export function timeZones() {
  try { if (Intl.supportedValuesOf) return Intl.supportedValuesOf('timeZone'); } catch (e) { /* older browsers */ }
  return ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Phoenix',
    'America/Anchorage', 'Pacific/Honolulu', 'Europe/London', 'Europe/Berlin', 'Asia/Kolkata', 'Asia/Singapore', 'Australia/Sydney', 'UTC'];
}

// A dropdown of the same zones the job form offers (Org setup → Locations, the Edit user panel). A value not in the
// browser's list (e.g. an older alias) stays selectable so nothing is lost.
export function TimeZoneSelect({ value, onChange, className, ariaLabel = 'Time zone', allowEmpty = true }) {
  const zones = useRef(null);
  if (!zones.current) zones.current = timeZones();
  const list = value && !zones.current.includes(value) ? [value, ...zones.current] : zones.current;
  return (
    <select aria-label={ariaLabel} className={className} value={value || ''} onChange={(e) => onChange(e.target.value)}>
      {allowEmpty && <option value="">Choose a time zone…</option>}
      {list.map((z) => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}
    </select>
  );
}

const box = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-brand-blue focus:outline-none';
const sub = 'block text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1.5';

function Label({ input, value, ctx }) {
  return (
    <span className="block text-xs font-bold text-gray-600 uppercase tracking-wide mb-1.5">
      {input.label}{isRequired(input, value, ctx) ? ' *' : ''}
    </span>
  );
}

function useDebounced(value, ms = 250) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

function TitleInput({ input, v, set, disabled }) {
  const [opts, setOpts] = useState([]);
  const q = useDebounced(v || '');
  useEffect(() => {
    let alive = true;
    intakeApi(`/suggest/titles?q=${encodeURIComponent(q)}`).then((r) => { if (alive) setOpts(r.titles || []); }).catch(() => {});
    return () => { alive = false; };
  }, [q]);
  return (
    <>
      <input aria-label={input.label} list="job-title-options" value={v || ''} disabled={disabled} className={box}
        placeholder="Start typing — e.g. Senior .NET Engineer" onChange={(e) => set(e.target.value)} />
      <datalist id="job-title-options">{opts.map((t) => <option key={t} value={t} />)}</datalist>
    </>
  );
}

// Locations (frame 02): pick country → state → city, "+ Add", added places as removable chips.
export function locationChip(loc, countryName, stateName) {
  const st = loc.state && /^[A-Z]{2}$/.test(loc.state) && loc.country === 'US' ? loc.state : stateName;
  return [loc.city, st, loc.country === 'US' ? 'USA' : (countryName || loc.country)].filter(Boolean).join(', ');
}

function LocationsInput({ input, v, set, disabled, attribution, offices = [] }) {
  const rows = Array.isArray(v) ? v.filter((r) => r && r.country) : [];
  const [countries, setCountries] = useState([]);
  const [names, setNames] = useState({});                   // "CC.STATE" → state name (for chips)
  const [pick, setPick] = useState({ country: 'US', state: '', city: '' });
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const q = useDebounced(pick.city || '');
  useEffect(() => { cachedGeo('/countries').then((r) => setCountries(r.countries || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!pick.country) { setStates([]); return; }
    cachedGeo(`/states?country=${pick.country}`).then((r) => {
      setStates(r.states || []);
      setNames((n) => ({ ...n, ...Object.fromEntries((r.states || []).map((s) => [`${pick.country}.${s.code}`, s.name])) }));
    }).catch(() => {});
  }, [pick.country]);
  useEffect(() => {
    if (!pick.country || (states.length && !pick.state)) { setCities([]); return; }
    geoApi(`/cities?country=${pick.country}&state=${encodeURIComponent(pick.state || '')}&q=${encodeURIComponent(q)}`)
      .then((r) => setCities(r.cities || [])).catch(() => {});
  }, [pick.country, pick.state, q, states.length]);
  const countryName = (cc) => (countries.find((c) => c.code === cc) || {}).name;
  const canAdd = pick.country && (!states.length || pick.state) && rows.length < (input.max_items || 10);
  const add = () => {
    if (!canAdd) return;
    set([...rows, { country: pick.country, state: pick.state, city: (pick.city || '').trim() }]);
    setPick({ ...pick, city: '' });
  };
  const same = (a, b) => a.country === b.country && (a.state || '') === (b.state || '') && (a.city || '').toLowerCase() === (b.city || '').toLowerCase();
  const freeOffices = offices.filter((o) => !rows.some((r) => same(r, o)));
  return (
    <div>
      {freeOffices.length > 0 && (
        <div className="mb-3" data-testid="office-picks">
          <span className={sub}>Your offices</span>
          <div className="flex flex-wrap gap-2 mt-1">
            {freeOffices.map((o) => (
              <button key={o.id} type="button" disabled={disabled || rows.length >= (input.max_items || 10)}
                onClick={() => set([...rows, { country: o.country, state: o.state || '', city: o.city }])}
                className="px-3 py-1.5 rounded-full border border-brand-blue text-brand-blue text-xs font-semibold hover:bg-blue-50 disabled:opacity-40">
                + {locationChip(o, countryName(o.country), names[`${o.country}.${o.state}`])}{o.label ? ` · ${o.label}` : ''}
              </button>
            ))}
          </div>
        </div>
      )}
      <p className="text-sm text-gray-600 mb-3">Pick country, then state, then city. Add as many locations as you need.</p>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end">
        <label className="block"><span className={sub}>Country</span>
          <select aria-label="Country" value={pick.country} disabled={disabled} className={box}
            onChange={(e) => setPick({ country: e.target.value, state: '', city: '' })}>
            <option value="">Country…</option>
            {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select></label>
        <label className="block"><span className={sub}>State</span>
          <select aria-label="State" value={pick.state} disabled={disabled || !states.length} className={box}
            onChange={(e) => setPick({ ...pick, state: e.target.value, city: '' })}>
            <option value="">{states.length ? 'State / region…' : '—'}</option>
            {states.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
          </select></label>
        <label className="block"><span className={sub}>City</span>
          <input aria-label="City" list="city-options" value={pick.city} disabled={disabled} placeholder="City (optional)"
            className={box} onChange={(e) => setPick({ ...pick, city: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
          <datalist id="city-options">{cities.map((c) => <option key={c} value={c} />)}</datalist></label>
        <button type="button" disabled={disabled || !canAdd} onClick={add}
          className="px-4 py-2 rounded-lg border border-brand-blue text-brand-blue text-sm font-semibold disabled:opacity-40">+ Add</button>
      </div>
      {rows.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3" data-testid="location-chips">
          {rows.map((r, i) => (
            <span key={`${r.country}.${r.state}.${r.city}.${i}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 text-brand-dark text-xs font-semibold">
              {locationChip(r, countryName(r.country), names[`${r.country}.${r.state}`])}
              <button type="button" disabled={disabled} aria-label={`Remove location ${i + 1}`}
                onClick={() => set(rows.filter((_, j) => j !== i))} className="text-brand-dark/60 hover:text-red-600">✕</button>
            </span>
          ))}
        </div>
      )}
      <p className="text-[10px] text-gray-400 mt-2" data-testid="geo-attribution">{attribution}</p>
    </div>
  );
}

// Skills (frame 03): ✨ suggestions on top, chosen skills as rows (years dropdown for primary), Enter to add your own.
export const YEARS = Array.from({ length: 15 }, (_, i) => i + 1);

function Suggestions({ title, suggestions, taken, onPick, disabled }) {
  const left = (suggestions || []).filter((s) => !taken.has(s.toLowerCase()));
  if (!left.length) return null;
  return (
    <div className="mb-3" data-testid="skill-suggestions">
      <p className="text-xs text-gray-600 mb-2">✨ Suggested{title ? ` for “${title}”` : ''} — click to add, or type your own.</p>
      <div className="flex flex-wrap gap-2">
        {left.map((s) => (
          <button key={s} type="button" disabled={disabled} onClick={() => onPick(s)}
            className="px-3 py-1.5 rounded-full border border-dashed border-brand-blue/50 text-brand-blue text-xs font-semibold hover:bg-blue-50">+ {s}</button>
        ))}
      </div>
    </div>
  );
}

function SkillList({ input, items, setItems, disabled, suggestions, title, withYears, exclude }) {
  const [text, setText] = useState('');
  const [editing, setEditing] = useState(null);          // { i, text } while a skill is being renamed
  const max = input.max_items || 5;
  const names = items.map((s) => (withYears ? s.name : s));
  const add = (name) => {
    const n = String(name || '').trim();
    if (!n || items.length >= max || names.some((x) => x.toLowerCase() === n.toLowerCase())) return;
    setItems([...items, withYears ? { name: n, years: '' } : n]);
    setText('');
  };
  const rename = () => {
    const n = String((editing || {}).text || '').trim();
    const i = (editing || {}).i;
    setEditing(null);
    if (!n || names.some((x, j) => j !== i && x.toLowerCase() === n.toLowerCase())) return;
    setItems(items.map((x, j) => (j === i ? (withYears ? { ...x, name: n } : n) : x)));
  };
  return (
    <div>
      <p className="text-right text-xs font-semibold text-gray-500 -mt-6 mb-2" data-testid="skill-count">{items.length} of {max}</p>
      {items.length < max && <Suggestions title={title} suggestions={suggestions} disabled={disabled} onPick={add}
        taken={new Set([...names, ...(exclude || [])].map((x) => x.toLowerCase()))} />}
      <div className="space-y-2">
        {items.map((s, i) => (
          <div key={`${names[i]}.${i}`} className="flex items-center gap-3 px-3 py-2 border border-gray-200 rounded-xl bg-white" data-testid="skill-row">
            {editing && editing.i === i ? (
              <input aria-label={`Rename ${names[i]}`} autoFocus value={editing.text} className={`${box} flex-1 py-1`}
                onChange={(e) => setEditing({ i, text: e.target.value })} onBlur={rename}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); rename(); } if (e.key === 'Escape') setEditing(null); }} />
            ) : <span className="flex-1 text-sm font-medium text-gray-900">{names[i]}</span>}
            {withYears && (
              <select aria-label={`Years of ${s.name}`} value={s.years ?? ''} disabled={disabled}
                onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, years: e.target.value === '' ? '' : Number(e.target.value) } : x)))}
                className={`px-2 py-1 rounded-lg text-xs font-semibold border ${s.years === '' || s.years == null ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-gray-200 bg-gray-50'}`}>
                <option value="">Years…</option>
                {YEARS.map((y) => <option key={y} value={y}>{y}+ yrs</option>)}
              </select>
            )}
            <button type="button" disabled={disabled} aria-label={`Rename ${names[i]}`} title="Rename"
              onClick={() => setEditing({ i, text: names[i] })} className="w-8 h-8 rounded-lg flex items-center justify-center text-brand-blue hover:bg-blue-50">
              <PencilSquareIcon className="w-4 h-4" />
            </button>
            <button type="button" disabled={disabled} aria-label={`Remove ${names[i]}`} title="Remove"
              onClick={() => setItems(items.filter((_, j) => j !== i))} className="w-8 h-8 rounded-lg flex items-center justify-center text-red-600 hover:bg-red-50">
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      {items.length < max ? (
        <input aria-label={input.label} value={text} disabled={disabled} placeholder="Type a skill and press Enter…"
          onChange={(e) => setText(e.target.value)} onBlur={() => add(text)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(text); } }} className={`${box} mt-2`} />
      ) : <p className="mt-2 text-xs font-semibold text-amber-700" data-testid="skill-max">Max {max} — remove one to add another</p>}
    </div>
  );
}

function SkillsInput({ input, v, set, disabled, suggestions, title }) {
  const items = (Array.isArray(v) ? v : []).filter((s) => s && String(s.name || '').trim());
  return <SkillList input={input} items={items} setItems={set} disabled={disabled} suggestions={suggestions} title={title} withYears />;
}

function ChipsInput({ input, v, set, disabled, suggestions, exclude, title }) {
  const items = Array.isArray(v) ? v : (typeof v === 'string' && v ? v.split(',').map((x) => x.trim()).filter(Boolean) : []);
  return <SkillList input={input} items={items} setItems={set} disabled={disabled} suggestions={suggestions} title={title} exclude={exclude} />;
}

// Field 18: the two fixed steps (shown, not editable) and the Quali5d technical screen's settings — the existing
// Phase 3 values (duration_minutes, coding_mode); the schedule form uses them
function TechScreenInput({ input, v, set, disabled }) {
  const val = v || {};
  const pick = (k, x) => set({ ...val, [k]: x });
  const chip = (on) => `px-3 py-1.5 rounded-lg border text-sm font-medium ${on ? 'bg-brand-blue text-white border-brand-blue' : 'bg-white text-gray-700 border-gray-200 hover:border-brand-blue'}`;
  return (
    <div data-testid="tech-screen">
      <ol className="space-y-1.5 mb-3">
        {(input.fixed_steps || []).map((s, i) => (
          <li key={s} className="flex items-center gap-2 text-sm text-gray-700" data-testid="fixed-step">
            <span className="w-5 h-5 rounded-full bg-gray-100 text-gray-500 text-[11px] font-bold flex items-center justify-center">{i + 1}</span>
            {s}<span className="text-[11px] text-gray-400">· fixed</span>
          </li>
        ))}
      </ol>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-7">
        <div><span className={sub}>Technical screen length</span>
          <div className="flex flex-wrap gap-2 mt-1" role="radiogroup" aria-label="Technical screen length">
            {(input.durations || []).map((m) => (
              <button key={m} type="button" role="radio" aria-checked={val.duration_minutes === m} disabled={disabled}
                onClick={() => pick('duration_minutes', m)} className={chip(val.duration_minutes === m)}>{m} min</button>
            ))}
          </div>
        </div>
        <div><span className={sub}>Interview type</span>
          <div className="flex flex-wrap gap-2 mt-1" role="radiogroup" aria-label="Interview type">
            {(input.coding_modes || []).map((o) => (
              <button key={o.value} type="button" role="radio" aria-checked={val.coding_mode === o.value} disabled={disabled}
                onClick={() => pick('coding_mode', o.value)} className={chip(val.coding_mode === o.value)}>{o.label}</button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Interviewers for one round: company users (searchable) or someone external (name + e-mail)
function InterviewerPicker({ people, setPeople, users, disabled, round }) {
  const [q, setQ] = useState('');
  const [ext, setExt] = useState(null);                 // { name, email } while adding someone external
  const taken = new Set(people.filter((p) => p.user_id).map((p) => p.user_id));
  const matches = q.trim() ? users.filter((u) => !taken.has(u.user_id) && u.name.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 6) : [];
  return (
    <div>
      <div className="flex flex-wrap gap-1.5">
        {people.map((p, i) => (
          <span key={`${p.user_id || p.name}.${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-brand-dark text-xs font-semibold">
            {p.name}{p.email ? ` (${p.email})` : ''}
            <button type="button" disabled={disabled} aria-label={`Remove ${p.name} from round ${round}`} title="Remove"
              onClick={() => setPeople(people.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-600">×</button>
          </span>
        ))}
      </div>
      {ext ? (
        <div className="flex gap-1.5 mt-1.5">
          <input aria-label={`External interviewer name, round ${round}`} placeholder="Name" value={ext.name} className={`${box} py-1`}
            onChange={(e) => setExt({ ...ext, name: e.target.value })} />
          <input aria-label={`External interviewer e-mail, round ${round}`} placeholder="E-mail" value={ext.email} className={`${box} py-1`}
            onChange={(e) => setExt({ ...ext, email: e.target.value })} />
          <button type="button" disabled={!ext.name.trim()} onClick={() => { setPeople([...people, { name: ext.name.trim(), email: ext.email.trim() || null }]); setExt(null); }}
            className="px-2 rounded-lg border border-brand-blue text-brand-blue text-xs font-semibold disabled:opacity-40">Add</button>
        </div>
      ) : (
        <div className="relative mt-1.5">
          <input aria-label={`Search interviewers, round ${round}`} placeholder="Search interviewers in your company…" value={q} disabled={disabled}
            className={`${box} py-1`} onChange={(e) => setQ(e.target.value)} />
          {matches.length > 0 && (
            <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
              {matches.map((u) => (
                <li key={u.user_id}><button type="button" className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50"
                  onClick={() => { setPeople([...people, { user_id: u.user_id, name: u.name }]); setQ(''); }}>{u.name}</button></li>
              ))}
            </ul>
          )}
          <button type="button" disabled={disabled} onClick={() => setExt({ name: '', email: '' })}
            className="mt-1 text-xs font-semibold text-brand-blue hover:underline">+ Someone outside the company</button>
        </div>
      )}
    </div>
  );
}

const ROUND_WORDS = (d) => (String(d || '').trim() ? String(d).trim().split(/\s+/).length : 0);

function RoundsInput({ input, v, set, disabled, users }) {
  const rows = Array.isArray(v) ? v : [];
  const max = input.max_items || 5;
  const words = input.description_words || 20;
  const patchRow = (i, p) => set(rows.map((r, j) => (j === i ? { ...r, ...p } : r)));
  const sel = 'px-2 py-1.5 border border-gray-200 rounded-lg text-sm bg-white';
  return (
    <div data-testid="interview-rounds">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-800">{input.label}</span>
        <span className="text-xs text-gray-500" data-testid="round-count">{rows.length} of {max}</span>
      </div>
      <div className="space-y-2 mt-2">
        {rows.map((r, i) => (
          <div key={i} className="border border-gray-200 rounded-xl p-2.5 bg-white" data-testid="round-row">
            <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_1fr_auto] gap-2 items-center">
              <span className="text-xs font-bold text-gray-500">{i + 3}.</span>
              <select aria-label={`Round ${i + 1} type`} value={r.type || ''} disabled={disabled} className={sel}
                onChange={(e) => patchRow(i, { type: e.target.value })}>
                <option value="">Round type…</option>
                {(input.round_types || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <select aria-label={`Round ${i + 1} format`} value={r.format || ''} disabled={disabled} className={sel}
                onChange={(e) => patchRow(i, { format: e.target.value })}>
                <option value="">Format…</option>
                {(input.formats || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <button type="button" disabled={disabled} aria-label={`Delete round ${i + 1}`} title="Delete round"
                onClick={() => set(rows.filter((_, j) => j !== i))} className="w-8 h-8 rounded-lg flex items-center justify-center text-red-600 hover:bg-red-50">
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
              <InterviewerPicker people={r.interviewers || []} setPeople={(p) => patchRow(i, { interviewers: p })} users={users} disabled={disabled} round={i + 1} />
              <div>
                <input aria-label={`Round ${i + 1} description`} value={r.description || ''} disabled={disabled} className={`${box} py-1`}
                  placeholder="e.g. System design with the architect" onChange={(e) => patchRow(i, { description: e.target.value })} />
                <p className={`text-[11px] mt-0.5 ${ROUND_WORDS(r.description) > words ? 'text-red-600' : 'text-gray-400'}`}>
                  {ROUND_WORDS(r.description)} / {words} words</p>
              </div>
            </div>
          </div>
        ))}
      </div>
      {rows.length < max ? (
        <button type="button" disabled={disabled} onClick={() => set([...rows, { type: '', interviewers: [], format: '', description: '' }])}
          className="mt-2 text-sm font-semibold text-brand-blue hover:underline">+ Add a round</button>
      ) : <p className="mt-2 text-xs text-gray-500">Max {max} rounds — delete one to add another</p>}
    </div>
  );
}

// Reports to: the HM by default, the department's people, or "Someone else…" (anyone in the company)
function ReportsToInput({ input, v, set, disabled, opts, value, label }) {
  const dept = (value || {}).department;
  const people = (dept && dept.id && (opts.members || {})[String(dept.id)]) || [];
  const me = opts.me;
  const [other, setOther] = useState(false);
  const [q, setQ] = useState('');
  useEffect(() => {                                     // default: the hiring manager themself
    if (!v && me && dept && dept.id) set({ user_id: me.user_id, name: me.name });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dept && dept.id]);
  if (!(opts.departments || []).length) {
    return <label className="block">{label}<input aria-label={input.label} value={(v && v.name) || v || ''} disabled={disabled}
      className={box} placeholder="Name" onChange={(e) => set(e.target.value)} /></label>;
  }
  const choices = [...(me ? [{ user_id: me.user_id, name: `${me.name} (you)` }] : []), ...people.filter((p) => !me || p.user_id !== me.user_id)];
  const current = v && v.user_id;
  const known = choices.some((c) => c.user_id === current);
  const matches = q.trim() ? (opts.users || []).filter((u) => u.name.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 8) : [];
  return (
    <div>{label}
      <select aria-label={input.label} value={other ? '__other' : (current || '')} disabled={disabled || !dept} className={box}
        onChange={(e) => {
          if (e.target.value === '__other') { setOther(true); return; }
          setOther(false);
          const p = choices.find((x) => String(x.user_id) === e.target.value);
          set(p ? { user_id: p.user_id, name: p.name.replace(/ \(you\)$/, '') } : null);
        }}>
        <option value="">{dept ? 'Choose a person…' : 'Choose the department first'}</option>
        {choices.map((p) => <option key={p.user_id} value={p.user_id}>{p.name}</option>)}
        {current && !known && <option value={current}>{v.name}</option>}
        <option value="__other">Someone else…</option>
      </select>
      {other && (
        <div className="relative mt-1.5">
          <input aria-label="Search everyone in your company" placeholder="Search everyone in your company…" value={q} className={box}
            onChange={(e) => setQ(e.target.value)} />
          {matches.length > 0 && (
            <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {matches.map((u) => (
                <li key={u.user_id}><button type="button" className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50"
                  onClick={() => { set({ user_id: u.user_id, name: u.name }); setOther(false); setQ(''); }}>{u.name}</button></li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export function FieldInput({ input, value, onChange, disabled, ctx = {} }) {
  const v = (value || {})[input.name];
  const set = (x) => onChange({ ...(value || {}), [input.name]: x });
  const label = <Label input={input} value={value} ctx={ctx} />;
  const required = isRequired(input, value, ctx);
  const opts = ctx.options || {};
  const tzRef = useRef(null);
  if (!tzRef.current) tzRef.current = timeZones();

  switch (input.kind) {
    case 'select':
      return (
        <div>{label}
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={input.label}>
            {input.options.map((o) => (
              <button key={o.value} type="button" role="radio" aria-checked={v === o.value} disabled={disabled}
                onClick={() => set(v === o.value && !required ? null : o.value)}
                className={`px-3 py-2 rounded-xl border text-sm text-left font-medium transition-colors ${v === o.value
                  ? 'bg-brand-blue text-white border-brand-blue' : 'bg-white text-gray-700 border-gray-200 hover:border-brand-blue'}`}>
                {o.label}
              </button>
            ))}
          </div>
        </div>
      );
    case 'title':
      return <label className="block">{label}<TitleInput input={input} v={v} set={set} disabled={disabled} /></label>;
    case 'level':
      return (
        <label className="block">{label}
          <input aria-label={input.label} list="job-level-options" value={v || ''} disabled={disabled} className={box}
            placeholder="Choose or type a level" onChange={(e) => set(e.target.value)} />
          <datalist id="job-level-options">{(opts.levels || []).map((l) => <option key={l} value={l} />)}</datalist>
        </label>
      );
    case 'tech_screen':
      return <TechScreenInput input={input} v={v} set={set} disabled={disabled} />;
    case 'interview_rounds':
      return <RoundsInput input={input} v={v} set={set} disabled={disabled} users={opts.interviewers || []} />;
    case 'department': {
      const depts = opts.departments || [];
      if (!depts.length) {
        return (
          <label className="block">{label}
            <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-1.5 mb-1.5" data-testid="no-departments">
              Ask your company admin to add departments.{' '}
              {opts.is_admin && <a href="/dashboard/company-admin" className="font-bold text-brand-blue underline">Add departments</a>}
            </p>
            <input aria-label={input.label} value={(v && v.name) || v || ''} disabled={disabled}
              className={box} placeholder="Department" onChange={(e) => set(e.target.value)} />
          </label>
        );
      }
      return (
        <label className="block">{label}
          <select aria-label={input.label} value={(v && v.id) || ''} disabled={disabled} className={box}
            onChange={(e) => {
              const d = depts.find((x) => String(x.id) === e.target.value);
              onChange({ ...(value || {}), department: d ? { id: d.id, name: d.name } : null, reporting_manager: null });
            }}>
            <option value="">Choose a department…</option>
            {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </label>
      );
    }
    case 'reports_to':
      return <ReportsToInput input={input} v={v} set={set} disabled={disabled} opts={opts} value={value} label={label} />;
    case 'date':
      return <label className="block">{label}<input aria-label={input.label} type="date" min={input.future_only ? todayIso() : undefined}
        value={v || ''} disabled={disabled} className={box} onChange={(e) => set(e.target.value)} /></label>;
    case 'time':
      return (
        <label className="block">{label}
          <select aria-label={input.label} value={v || ''} disabled={disabled} className={box} onChange={(e) => set(e.target.value || null)}>
            <option value="">—</option>
            {(ctx.halfHours || []).map((t) => <option key={t} value={t}>{timeLabel(t)}</option>)}
          </select>
        </label>
      );
    case 'timezone':
      return (
        <label className="block">{label}
          <input aria-label={input.label} list="tz-options" value={v || ''} disabled={disabled} className={box}
            placeholder="Type to search — e.g. Chicago" onChange={(e) => set(e.target.value)} />
          <datalist id="tz-options">{tzRef.current.map((z) => <option key={z} value={z} />)}</datalist>
        </label>
      );
    case 'locations':
      return <div>{label}<LocationsInput input={input} v={v} set={set} disabled={disabled} attribution={ctx.geoAttribution} offices={opts.offices || []} /></div>;
    case 'skills':
      return <div>{label}<SkillsInput input={input} v={v} set={set} disabled={disabled} suggestions={ctx.skillSuggestions} title={ctx.jobTitle} /></div>;
    case 'chips':
      return <div>{label}<ChipsInput input={input} v={v} set={set} disabled={disabled} suggestions={ctx.skillSuggestions} exclude={ctx.primarySkillNames} title={ctx.jobTitle} /></div>;
    case 'dependent_select': {
      const parent = (value || {})[input.depends_on];
      const choices = (input.options_by || {})[parent] || [];
      return (
        <label className="block">{label}
          <select aria-label={input.label} value={v || ''} disabled={disabled} className={box} onChange={(e) => set(e.target.value || null)}>
            <option value="">Choose…</option>
            {choices.map((c) => <option key={c} value={c}>{c}</option>)}
            {input.other && <option value="other">Other</option>}
          </select>
        </label>
      );
    }
    case 'textarea':
      return <label className="block">{label}<textarea aria-label={input.label} rows={3} value={v || ''} disabled={disabled}
        className={box} onChange={(e) => set(e.target.value)} /></label>;
    case 'number':
      return <label className="block">{label}<input aria-label={input.label} type="number" value={v ?? ''} min={input.min} max={input.max}
        disabled={disabled} className={box} onChange={(e) => set(e.target.value === '' ? null : Number(e.target.value))} /></label>;
    default:
      return <label className="block">{label}<input aria-label={input.label} type="text" value={v || ''} disabled={disabled}
        placeholder={input.placeholder || ''} className={box} onChange={(e) => set(e.target.value)} /></label>;
  }
}
